import { AxiosRequestConfig, Method } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { EntitySchema as PlatformStackEntitySchema } from '@sierralabs/entity-core';

import { ApiParams, FetchError } from '../../common/api/api';
import {
  ApiResult,
  FetchApiCallback,
  useApiGet,
  useApiPatch
} from '../../common/api/Api.hooks';
import { htmlToJira, htmlToMd } from '../../common/formatter';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { createViewUrl } from '../documents/Document.hook';
import { EntitySchema } from '../entity/types/entity-schema';
import { parseTaskStatus } from '../tasks/task.helper';
import { Task } from '../tasks/Task.interface';
import { JiraListFilterOptions } from './components/JiraListFilter';
import {
  jiraConnectRequest,
  useJiraConnectRequest
} from './hooks/useJiraConnectRequest';
import { useJiraConnectContext } from './JiraConnect.context';
import {
  JiraConnect,
  JiraConnectAPI,
  JiraConnectRequestOptions,
  JiraConnectResult,
  JiraIssue,
  JiraIssueCount,
  JiraIssueCreateDto,
  JiraIssueCreateResponse,
  JiraIssueFields,
  JiraIssueMetaData,
  JiraIssues,
  JiraIssueSearch,
  JiraIssueTransitions,
  JiraIssueType,
  JiraOpenApi,
  JiraProject,
  JiraStatus,
  UpdateJiraIssuesParams,
  UpdateJiraIssuesResult
} from './JiraConnect.interface';

export function useJiraConnectAPI(): {
  connect: JiraConnectAPI | undefined;
  isAvailable: boolean;
} {
  const { connect, isAvailable } = useJiraConnectContext();
  return { connect, isAvailable };
}

export function useJiraConnectToken(): string | undefined {
  const { token } = useJiraConnectContext();
  return token;
}

export function filterJiraIssueFields<
  T extends {
    fields?: { summary?: string; description?: string; [key: string]: any };
  }
>(issue: T): T {
  if (!issue.fields) {
    return issue;
  }
  issue = { ...issue };
  issue.fields = { ...issue.fields };
  const { fields } = issue;
  if (fields.summary) {
    // convert to markdown to remove html tags
    const tagIndex = fields.summary.indexOf('<');
    if (tagIndex > -1 && fields.summary.indexOf('>', tagIndex + 1) > -1) {
      fields.summary = htmlToMd(fields.summary);
    }
    fields.summary = fields.summary.replace(/\n/g, ' ');
  }
  if (fields.description) {
    fields.description = htmlToJira(fields.description);
  }
  return issue;
}

export function useCreateJiraIssue(): [
  JiraConnectResult<JiraIssueCreateResponse>,
  (issue: JiraIssueCreateDto) => void
] {
  const [result, request] = useJiraConnectRequest<JiraIssueCreateResponse>();
  const createIssue = useCallback(
    (issue: JiraIssueCreateDto) => {
      request({
        type: 'POST',
        url: '/rest/api/2/issue',
        contentType: 'application/json',
        data: JSON.stringify(filterJiraIssueFields(issue))
      });
    },
    [request]
  );
  return [result, createIssue];
}

export function useGetJiraStatuses(): [
  JiraConnectResult<JiraStatus[]>,
  () => void
] {
  const [result, request] = useJiraConnectRequest<JiraStatus[]>();
  const getStatus = useCallback(() => {
    request({ url: '/rest/api/2/status' });
  }, [request]);
  return [result, getStatus];
}

export function useGetJiraIssueTypes(): [
  JiraConnectResult<JiraIssueType[]>,
  () => void
] {
  const [result, request] = useJiraConnectRequest<JiraIssueType[]>();
  const getIssueType = useCallback(() => {
    request({ url: '/rest/api/2/issuetype' });
  }, [request]);
  return [result, getIssueType];
}

export function useGetJiraIssueTypesByProject(): [
  JiraConnectResult<JiraIssueType[]>,
  (options?: Omit<JiraConnectRequestOptions, 'url'>) => void
] {
  const [result, request] = useJiraConnectRequest<JiraIssueType[]>();
  const getIssueTypes = useCallback(
    (options?: Omit<JiraConnectRequestOptions, 'url'>) => {
      request({ url: '/rest/api/2/issuetype/project', ...options });
    },
    [request]
  );
  return [result, getIssueTypes];
}

export function useGetJiraProjects(): [
  JiraConnectResult<JiraProject[]>,
  () => void
] {
  const [result, request] = useJiraConnectRequest<JiraProject[]>();
  const getProjects = useCallback(() => {
    request({ url: '/rest/api/2/project' });
  }, [request]);
  return [result, getProjects];
}

export function useGetFieldsByProjectAndIssueType(
  projectId: string,
  issueTypeId: string
): [JiraConnectResult<JiraIssueMetaData>, () => void] {
  const [result, request] = useJiraConnectRequest<JiraIssueMetaData>();
  const getIssueFields = useCallback(() => {
    request({
      url: `/rest/api/2/issue/createmeta?projectIds=${projectId}&issuetypeIds=${issueTypeId}&expand=projects.issuetypes.fields`
    });
  }, [issueTypeId, projectId, request]);
  return [result, getIssueFields];
}

// endpoint returns issue types with statuses prop
export function useGetJiraStatusesByProjectId(
  projectId: string
): [JiraConnectResult<JiraIssueType[]>, () => void] {
  const [result, request] = useJiraConnectRequest<JiraIssueType[]>();
  const getStatuses = useCallback(() => {
    projectId && request({ url: `/rest/api/2/project/${projectId}/statuses` });
  }, [projectId, request]);
  return [result, getStatuses];
}

/** @deprecated Use {@linkcode useUpdateJiraIssues} instead. */
export function useUpdateJiraIssue(): [
  JiraConnectResult<any>,
  (updateDto: any, issueId: number) => void
] {
  const [result, request] = useJiraConnectRequest<any>();
  const updateIssue = useCallback(
    (updateDto: any, issueId: number) => {
      request({
        type: 'PUT',
        url: `/rest/api/2/issue/${issueId}`,
        contentType: 'application/json',
        data: JSON.stringify(filterJiraIssueFields(updateDto))
      });
    },
    [request]
  );
  return [result, updateIssue];
}

export function useUpdateJiraIssues(
  version: string | number = 2
): [
  UpdateJiraIssuesResult,
  (updates: UpdateJiraIssuesParams[]) => Promise<void>
] {
  const [result, setResult] = useState<UpdateJiraIssuesResult>({
    isLoading: false,
    success: false,
    errors: []
  });
  // bulk update to avoid workarounds
  const updateIssues = useCallback(
    async (updates: UpdateJiraIssuesParams[]) => {
      setResult({ isLoading: true, success: false, errors: [] });
      const promises = updates.map(update => {
        const { dto, issueId } = update;
        return jiraConnectRequest<any>({
          type: 'PUT',
          url: `/rest/api/${version}/issue/${issueId}`,
          contentType: 'application/json',
          data: JSON.stringify(filterJiraIssueFields(dto))
        });
      });
      const results = await Promise.all(promises);
      const errors = results
        .map(result => result.error)
        .filter((error): error is FetchError => !!error);
      setResult({ isLoading: false, success: true, errors });
    },
    [version]
  );
  return [result, updateIssues];
}

export function useFindJiraIssueFields(): [
  JiraConnectResult<JiraIssueFields>,
  (options?: Omit<JiraConnectRequestOptions, 'url'>) => void
] {
  const [result, request] = useJiraConnectRequest<JiraIssueFields>();
  const getIssueFields = useCallback(
    (options?: Omit<JiraConnectRequestOptions, 'url'>) => {
      request({ url: '/rest/api/2/field/search', ...options });
    },
    [request]
  );
  return [result, getIssueFields];
}

const issueKeyRegExp: RegExp = /^[\w\d]+-[\d]+$/i;

export function getJiraIssueSearchCondition(
  search: string | null | undefined
): [string | undefined, boolean] {
  let condition: string | undefined;
  let validateQuery: boolean = true;
  search = (search || '').trim();
  if (search) {
    search = search.replace(/"/g, '');
    condition = `summary ~ "${search}*"`;
    if (issueKeyRegExp.test(search)) {
      condition += ` OR key = "${search}"`;
      // prevent query error for invalid key
      validateQuery = false;
    }
  }
  return [condition, validateQuery];
}

export interface JiraIssueJqlOptions {
  sort?: string;
  direction?: string;
  title?: string;
  keys?: string[]; // match issue.key
  filter?: JiraListFilterOptions;
}

export function getJiraIssueJql(options: JiraIssueJqlOptions) {
  const {
    sort = 'key',
    direction = 'ASC',
    title = null,
    keys = [],
    filter
  } = options;
  const { project, status, issueType } = filter || {};
  // create where statement
  const where: string[] = [];
  // fallback to a dummy search to bypass
  // the required bounded jql that requires search
  const [search = 'created <= now()'] = getJiraIssueSearchCondition(title);
  search && where.push(`(${search})`);
  project && where.push(`project = "${project}"`);
  status && where.push(`status = "${status}"`);
  issueType && where.push(`issuetype = "${issueType}"`);
  if (keys.length > 0) {
    const keysStr = keys.map(key => `"${key}"`).join(',');
    where.push(`key IN (${keysStr})`);
  }
  const whereStr = where.join(' AND ');
  return `${whereStr} ORDER BY ${sort} ${direction}`.trim();
}

export interface JiraIssuesFilterOptions extends JiraIssueJqlOptions {
  page?: number;
  limit?: number;
  jql?: string;
  data?: {
    expand?: string[];
    nextPageToken?: string | null;
    /**
     * Strong consistency issue IDs to be reconciled with search results.
     * Accepts max 50 IDs.
     */
    reconcileIssues?: number[];
  };
}

export function getFindJiraIssuesRequestOptions(
  options: JiraIssuesFilterOptions
): JiraConnectRequestOptions {
  return {
    type: 'POST',
    url: '/rest/api/3/search/jql',
    contentType: 'application/json',
    data: {
      ...options.data,
      expand: options.data?.expand?.join(','),
      fields: ['*all'],
      jql: options.jql || getJiraIssueJql(options),
      maxResults: options.limit ?? 10
    }
  };
}

export function useFindJiraIssues(): [
  JiraConnectResult<PaginatedList<JiraIssue>>,
  (
    options: JiraIssuesFilterOptions
  ) => Promise<{ data?: PaginatedList<JiraIssue>; error?: FetchError }>,
  (options: JiraIssuesFilterOptions) => JiraIssuesFilterOptions
] {
  // when jql changes, page tokens must be reset since they would be invalid
  const cachedJqlRef = useRef<string>();
  // access page index via page tokens (`nextPageToken`)
  // https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-issue-search/#api-rest-api-3-search-jql-post
  const pageTokensRef = useRef<(string | null)[]>([null]);
  const [state, setState] = useState<{
    data?: PaginatedList<JiraIssue>;
    isLoading: boolean;
    error?: FetchError;
  }>({ isLoading: false });

  const getFilterOptions = useCallback((options: JiraIssuesFilterOptions) => {
    let { page = 0 } = options;
    let nextPageToken = pageTokensRef.current[page];
    // if the page token does not exist yet, go back to the first page
    if (nextPageToken === undefined) {
      page = 0;
      nextPageToken = null;
    }
    return { ...options, page, data: { ...options.data, nextPageToken } };
  }, []);

  const findIssues = useCallback(
    async (options: JiraIssuesFilterOptions) => {
      // if jql changes, reset page tokens
      const jql = getJiraIssueJql(options);
      if (cachedJqlRef.current !== undefined && cachedJqlRef.current !== jql) {
        pageTokensRef.current.length = 0;
      }
      cachedJqlRef.current = jql;

      options = getFilterOptions(options);
      options.jql = jql;

      setState(state => ({ ...state, isLoading: true, error: undefined }));

      const requestOptions = getFindJiraIssuesRequestOptions(options);
      requestOptions.data = JSON.stringify(requestOptions.data);

      const jiraIssueSearch = await jiraConnectRequest<JiraIssueSearch>(
        requestOptions
      );

      if (jiraIssueSearch.error || !jiraIssueSearch.data) {
        const { error } = jiraIssueSearch;
        setState(state => ({ ...state, isLoading: false, error }));
        return { error };
      }

      // set next page token
      const { page = 0, limit = 10 } = options;
      pageTokensRef.current[page + 1] =
        jiraIssueSearch.data.nextPageToken || null;

      // get count for total issues
      const jiraIssueCount = await jiraConnectRequest<JiraIssueCount>({
        type: 'POST',
        url: '/rest/api/3/search/approximate-count',
        contentType: 'application/json',
        data: JSON.stringify({ jql })
      });

      if (jiraIssueCount.error || !jiraIssueCount.data) {
        const { error } = jiraIssueCount;
        setState(state => ({ ...state, isLoading: false, error }));
        return { error };
      }

      const data: PaginatedList<JiraIssue> = {
        total: jiraIssueCount.data.count,
        data: jiraIssueSearch.data.issues,
        page,
        limit
      };

      setState({ data, isLoading: false });

      return { data };
    },
    [getFilterOptions]
  );

  return [state, findIssues, getFilterOptions];
}

export function useExportJiraIssuesUrl(options: JiraIssuesFilterOptions) {
  const token: string | undefined = useJiraConnectToken();

  const getViewUrl = useCallback(
    (params?: ApiParams, fileName?: string) => {
      return createViewUrl({
        isDocument: true,
        getUrl: (authToken: string) => {
          const jwtToken: string = token ? `JWT ${token}` : '';
          const requestOptions: JiraConnectRequestOptions =
            getFindJiraIssuesRequestOptions(options);
          const config: AxiosRequestConfig = {
            method: requestOptions.type as Method | undefined,
            url: requestOptions.url,
            data: requestOptions.data,
            headers: {
              ...requestOptions.headers,
              'Content-Type': requestOptions.contentType
            }
          };
          const allParams: URLSearchParams = new URLSearchParams({
            authToken,
            jwtToken,
            config: JSON.stringify(config)
          });
          if (params) {
            Object.entries(params).forEach(([key, value]) => {
              allParams.set(
                key,
                typeof value === 'object' ? JSON.stringify(value) : value
              );
            });
          }
          let paramsStr: string = allParams.toString();
          paramsStr = paramsStr && `?${paramsStr}`;
          const namePath: string = fileName ? `/${encodeURI(fileName)}` : '';
          return `jira-connect/export${namePath}${paramsStr}`;
        }
      });
    },
    [token, options]
  );

  return { getViewUrl };
}

export function useFindJiraConnect(): [
  ApiResult<JiraConnect>,
  FetchApiCallback
] {
  const { token, isAvailable } = useJiraConnectContext();
  const { get, ...result } = useApiGet<JiraConnect>('jira-connect/entity');

  const findJiraConnect = useCallback(
    (params?: ApiParams) => {
      // fetch for JiraConnect entity only if token exists
      token && isAvailable && get(params);
    },
    [token, isAvailable, get]
  );

  return [result, findJiraConnect];
}

export function useGetJiraApi(): [ApiResult<JiraOpenApi>, FetchApiCallback] {
  const { get, ...result } = useApiGet<JiraOpenApi>('jira-connect/api');
  return [result, get];
}

export function useUpdateJiraConnect(): [
  ApiResult<JiraConnect>,
  (dto: Partial<JiraConnect>) => void
] {
  const { patch, ...result } = useApiPatch<JiraConnect>('jira-connect/entity');
  const update = useCallback(
    (dto: Partial<JiraConnect>) => {
      patch(dto);
    },
    [patch]
  );
  return [result, update];
}

export function useGetJiraIssueTransitions(): [
  JiraConnectResult<JiraIssueTransitions>,
  (key: string) => Promise<JiraIssueTransitions | undefined>
] {
  const [result, request] = useJiraConnectRequest<JiraIssueTransitions>();
  const getTransitions = useCallback(
    async (key: string) => {
      const result = await request({
        url: `/rest/api/2/issue/${key}/transitions`
      });
      return result.data;
    },
    [request]
  );
  return [result, getTransitions];
}

export function useTransitionJiraIssue(): [
  JiraConnectResult<unknown>,
  (key: string, transitionId: string) => Promise<void>
] {
  const [result, request] = useJiraConnectRequest();
  const transition = useCallback(
    async (key: string, transitionId: string) => {
      await request({
        type: 'POST',
        url: `/rest/api/2/issue/${key}/transitions`,
        contentType: 'application/json',
        data: JSON.stringify({ transition: { id: transitionId } })
      });
    },
    [request]
  );
  return [result, transition];
}

export function useGetJiraIssuesByIds(): [
  JiraConnectResult<JiraIssues>,
  (ids: string[]) => void
] {
  const [result, request] = useJiraConnectRequest<JiraIssues>();

  const getIds = useCallback(
    (ids: string[]) => {
      // handle no ids
      if (ids.length === 0) {
        return;
      }

      const keys: string = ids.map((id: string) => `"${id}"`).join(',');
      request({
        type: 'POST',
        url: '/rest/api/3/search/jql',
        contentType: 'application/json',
        data: JSON.stringify({ fields: ['*all'], jql: `key IN (${keys})` })
      });
    },
    [request]
  );
  return [result, getIds];
}

const slashRegExp: RegExp = /[\/]{2,}/g;

export function useJiraAcRoute() {
  const history = useHistory();
  const { pathname, search, hash } = useLocation();
  const [acRoute = ''] = useQueryParam('ac.route', StringParam);
  const mountRef = useRef(true);
  const lastStateRef = useRef('');
  const state =
    typeof window.AP?.history?.getState === 'function'
      ? AP.history.getState()
      : null;
  /* Handle Parent Window Back/Forward buttons */
  useEffect(() => {
    if (window.AP?.history) {
      console.debug('CURRENT STATE: ', state);
      if (mountRef.current) {
        // handle initial iframe load to check state (i.e. hashbang)
        mountRef.current = false;
        if (state && state !== pathname) {
          history.replace(state);
        }
        (window.AP.history as any).popState((e: any) => {
          console.debug(
            `The URL has changed from: ${e.oldURL} to: ${e.newURL}`
          );
          console.debug('location.pathname:', pathname);
          if (e.oldURL !== e.newURL && e.newURL !== lastStateRef.current) {
            console.debug('history.replace ' + e.newURL);
            lastStateRef.current = e.newURL;
            history.replace(e.newURL);
          }
        });
      } else if (state !== pathname && lastStateRef.current !== pathname) {
        // pushState() everytime react router changes location
        console.debug('location change', pathname);
        lastStateRef.current = pathname;
        window.AP.history.pushState(pathname as any, pathname, pathname);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, state]);

  /* Handle Email Deep linking */
  useEffect(() => {
    if (acRoute && !pathname.endsWith(acRoute)) {
      console.debug('found acRoute:', acRoute);
      // combine query params
      const [acRoutePath, acParamsStr = ''] = acRoute.split('?');
      const separator: string = search ? '&' : '?';
      const mergedParams: string =
        search + (acParamsStr && separator + acParamsStr);
      // remove ac.route query
      const params: URLSearchParams = new URLSearchParams(mergedParams);
      params.delete('ac.route');
      let paramsStr: string = params.toString();
      paramsStr = paramsStr && `?${paramsStr}`;

      // use full relative url, trim multiple slashes
      const route: string = acRoutePath
        .replace(slashRegExp, '/')
        .concat(paramsStr + hash);
      console.log('acRoute redirecting to:', route);
      history.replace(route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only redirect once
}

export function createServletUrl(
  jiraConnect: JiraConnect,
  pageKey: string,
  acRoute?: string
): URL {
  const { baseUrl = '', key = '' } = jiraConnect;
  const url: URL = new URL(`/plugins/servlet/ac/${key}/${pageKey}`, baseUrl);
  if (acRoute) {
    url.searchParams.set('ac.route', acRoute);
  }
  return url;
}

export function getJiraIssueEntityFieldOptions<
  // accept both qms and platform stack EntitySchema types
  TSchema extends EntitySchema | PlatformStackEntitySchema
>(options: {
  entities: TSchema[] | undefined;
  entity: TSchema | undefined;
  /** @default 'Task' */
  taskSchemaName?: string;
}) {
  const { entities, entity, taskSchemaName = 'Task' } = options;
  const columns = (entity?.columns || []).map(column => {
    // NOTE: use column property name of column name instead since this is
    // used in createJiraIssueFields to access the taskEntity by property
    return { displayName: column.displayName, columnName: column.name };
  });
  type RelationshipSchema = Exclude<
    EntitySchema['relations'] | PlatformStackEntitySchema['relations'],
    undefined
  >[number];
  // get task relation
  const taskRelation = (
    entity?.relations as RelationshipSchema[] | undefined
  )?.find(relation => relation.target === taskSchemaName);
  const taskSchema = taskRelation
    ? entities?.find(schema => schema.name === taskRelation.target)
    : undefined;
  if (taskRelation && taskSchema) {
    // include task columns
    const taskColumns: (keyof Task)[] = ['key', 'status'];
    for (const column of taskSchema.columns) {
      if (taskColumns.includes(column.name as keyof Task)) {
        // NOTE: use column property name of column name instead since this is
        // used in createJiraIssueFields to access the taskEntity by property
        columns.push({
          displayName: `${taskSchema.displayName} - ${column.displayName}`,
          columnName: `${taskRelation.propertyName}.${column.name}`
        });
      }
    }
  }
  return columns;
}

export function createJiraIssueFields(
  value: Record<string, any>,
  fields: {
    /** The entity database column name to set to the Jira issue field. */
    entityField?: string;
    /** The Jira issue field to be set with the entity field value. */
    issueField?: string;
  }[]
) {
  const issueFields: Partial<JiraIssueCreateDto['fields']> = {};
  for (const field of fields) {
    // the current object
    let current = value;
    // parts of the entity field (e.g. task.key)
    const relations = (field.entityField || '').split('.');
    // remove last property since it is used for accessing the value
    const lastProperty = relations.pop();

    // handle relation entityFields (e.g. task.key)
    for (const relation of relations) {
      // next will be the new current object if it exists
      const next = current[relation];
      if (next && typeof next === 'object' && !Array.isArray(next)) {
        current = next;
      }
    }

    // set the value using the current object
    if (lastProperty && field.issueField) {
      let value = current[lastProperty];
      // handle task status (i.e. task.status, taskV2.status)
      if (
        relations.length === 1 &&
        relations[0].startsWith('task') &&
        lastProperty === 'status' &&
        typeof value === 'string'
      ) {
        value = parseTaskStatus(value);
      }

      const key = field.issueField as keyof typeof issueFields;
      issueFields[key] = value;
    }
  }
  return issueFields;
}
